import {TYPESENSE_PRIMARY_CLUSTER_HOST} from "@buildwithflux/constants";
import {createTypesenseClient} from "@buildwithflux/search";
import {useEffect, useState} from "react";
import type {Client} from "typesense";

import {getCurrentIdToken} from "../firebase/firebaseClient";
import {getAccountsApiBase} from "../utils/apiBase";
import {fetchWithRetry} from "../utils/fetchWithRetry";

import type {ClientInitialization} from "./clientInitialization";

// Cloud cluster host, used when the auth endpoint falls back to a cloud key
// (environments without a primary-cluster search-only key configured).
const TYPESENSE_CLOUD_HOST = "8iq4spr5t63o1vlgp.a1.typesense.net";
const CREDENTIAL_REFRESH_MS = 5 * 60 * 1_000;

interface AccountsViewAuthResponse {
    scopedSearchKey: string;
    collection: string;
    cluster: "cloud" | "primary";
}

export interface AccountsViewClient {
    client: Client;
    collection: string;
}

/**
 * Tier-aware accounts-view Typesense client for user/org search. The auth
 * endpoint scopes the key and picks the collection from the bearer token:
 * `accounts-view-public` for anonymous viewers, `accounts-view-users` for
 * signed-in viewers (so visibility-gated fields project per viewer).
 *
 * Re-fetches whenever `viewerUid` changes so the tier follows auth state. Pass
 * `enabled: false` on surfaces that never search accounts, to skip the auth call.
 */
export function useAccountsViewClientInitialization(
    viewerUid: string | null,
    enabled = true,
): ClientInitialization<AccountsViewClient> {
    const [result, setResult] = useState<ClientInitialization<AccountsViewClient>>({
        client: null,
        status: enabled ? "loading" : "disabled",
    });

    useEffect(() => {
        if (!enabled) {
            setResult({client: null, status: "disabled"});
            return;
        }
        let cancelled = false;
        let inFlight = false;
        let lastRequestedAt = 0;
        let lastCredentialFingerprint: string | undefined;
        setResult({client: null, status: "loading"});

        const refresh = async () => {
            if (inFlight || document.visibilityState !== "visible") return;
            inFlight = true;
            lastRequestedAt = Date.now();
            try {
                const token = await getCurrentIdToken();
                const res = await fetchWithRetry(() =>
                    fetch(`${getAccountsApiBase()}/accounts/auth/typesense-accounts`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            ...(token ? {Authorization: `Bearer ${token}`} : {}),
                        },
                        body: JSON.stringify({}),
                    }),
                );
                if (!res.ok) {
                    throw new Error(`Accounts-view auth failed: ${res.status}`);
                }
                const data = (await res.json()) as AccountsViewAuthResponse;
                if (cancelled) return;
                const fingerprint = `${data.cluster}|${data.collection}|${data.scopedSearchKey}`;
                if (fingerprint === lastCredentialFingerprint) return;
                lastCredentialFingerprint = fingerprint;
                setResult({
                    client: {
                        client: createTypesenseClient({
                            host: data.cluster === "primary" ? TYPESENSE_PRIMARY_CLUSTER_HOST : TYPESENSE_CLOUD_HOST,
                            port: 443,
                            protocol: "https",
                            apiKey: data.scopedSearchKey,
                            cacheSearchResultsForSeconds: 10,
                        }),
                        collection: data.collection,
                    },
                    status: "ready",
                });
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error("Failed to initialize accounts-view client:", err);
                setResult((previous) => (previous.client ? previous : {client: null, status: "error"}));
            } finally {
                inFlight = false;
            }
        };
        void refresh();
        const timer = setInterval(() => void refresh(), CREDENTIAL_REFRESH_MS);
        const refreshOnVisible = () => {
            if (document.visibilityState === "visible" && Date.now() - lastRequestedAt >= CREDENTIAL_REFRESH_MS) {
                void refresh();
            }
        };
        document.addEventListener("visibilitychange", refreshOnVisible);

        return () => {
            cancelled = true;
            clearInterval(timer);
            document.removeEventListener("visibilitychange", refreshOnVisible);
        };
    }, [viewerUid, enabled]);

    return result;
}

export function useAccountsViewClient(viewerUid: string | null, enabled = true): AccountsViewClient | null {
    return useAccountsViewClientInitialization(viewerUid, enabled).client;
}
